import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Divider, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { OrgContext } from '../contexts/OrgContext';
import { getProductsByOrgId } from '../services/productService';
import { getCustomers } from '../services/customerService';
import { getSuppliers } from '../services/supplierService';
import { getInventoryItemsByOrgId } from '../services/inventoryService';
import { getOrders } from '../services/orderService';

// Helper to normalize API responses that may be array or { data: [] }
const getArray = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

export default function OnboardingGuide({ defaultCollapsed, hidden, initialStatuses }) {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();
  const userKey = useMemo(() => localStorage.getItem('userEmail') || 'anon', []);
  const [collapsed, setCollapsed] = useState(() => {
    // If a defaultCollapsed prop is provided, prefer it on first mount
    if (typeof defaultCollapsed === 'boolean') {
      return defaultCollapsed;
    }
    try {
      const raw = localStorage.getItem(`onboardingGuide.collapsed:${userKey}`);
      return raw ? JSON.parse(raw) : true; // collapsed by default
    } catch {
      return true;
    }
  });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState(() => initialStatuses || {
    ADD_PRODUCT: false,
    ADD_CUSTOMER_OR_SUPPLIER: false,
    ADD_INVENTORY: false,
    CREATE_SALES_ORDER: false,
    CREATE_PURCHASE_ORDER: false,
  });

  const tasks = useMemo(() => [
    {
      code: 'ADD_PRODUCT',
      title: 'Add a product',
      description: 'Create your first product to start tracking inventory.',
      link: orgId ? `/${orgId}/products` : '#',
    },
    {
      code: 'ADD_CUSTOMER_OR_SUPPLIER',
      title: 'Add a customer or supplier',
      description: 'Create at least one customer or supplier to manage orders.',
      link: orgId ? `/${orgId}/customers` : '#', // Users can switch to suppliers tab from sidebar
    },
    {
      code: 'ADD_INVENTORY',
      title: 'Add inventory',
      description: 'Add stock to your products to enable sales and purchases.',
      link: orgId ? `/${orgId}/inventory` : '#',
    },
    {
      code: 'CREATE_SALES_ORDER',
      title: 'Create a sales order',
      description: 'Record a sale with at least one line item.',
      link: orgId ? `/${orgId}/orders/add` : '#',
    },
    {
      code: 'CREATE_PURCHASE_ORDER',
      title: 'Create a purchase order',
      description: 'Record a purchase from any supplier.',
      link: orgId ? `/${orgId}/orders/add` : '#',
    },
  ], [orgId]);

  const refresh = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [productsResp, customersResp, suppliersResp, inventoryResp, ordersResp] = await Promise.all([
        getProductsByOrgId(orgId, { limit: 1, page: 1 }),
        getCustomers(orgId, { limit: 1, page: 1 }),
        getSuppliers(orgId, { limit: 1, page: 1 }),
        getInventoryItemsByOrgId(orgId, { limit: 10, page: 1 }),
        getOrders({ limit: 50, page: 1 }),
      ]);

      const products = getArray(productsResp);
      const customers = getArray(customersResp);
      const suppliers = getArray(suppliersResp);
      const inventory = getArray(inventoryResp);
      const orders = getArray(ordersResp);

      const hasProduct = products.length > 0;
      const hasCustomerOrSupplier = customers.length > 0 || suppliers.length > 0;
      const hasInventory = inventory.length > 0 && inventory.some(i => (i.quantity_on_hand ?? i.quantity ?? 0) > 0);
      const hasSalesOrder = orders.some(o => (o.order_type || o.type) === 'Sale' || (o.order_type || o.type) === 'SALE');
      const hasPurchaseOrder = orders.some(o => (o.order_type || o.type) === 'Purchase' || (o.order_type || o.type) === 'PURCHASE');

      setStatuses({
        ADD_PRODUCT: hasProduct,
        ADD_CUSTOMER_OR_SUPPLIER: hasCustomerOrSupplier,
        ADD_INVENTORY: hasInventory,
        CREATE_SALES_ORDER: hasSalesOrder,
        CREATE_PURCHASE_ORDER: hasPurchaseOrder,
      });
    } catch (e) {
      // Soft-fail: keep previous statuses
      console.error('OnboardingGuide refresh failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    const visHandler = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', visHandler);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', visHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const completed = Object.values(statuses).filter(Boolean).length;
  const total = tasks.length;

  if (hidden) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Getting Started</Typography>
          <Box sx={{ fontSize: 13, opacity: 0.8 }}>{completed}/{total} complete</Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const next = !collapsed;
              setCollapsed(next);
              try {
                localStorage.setItem(`onboardingGuide.collapsed:${userKey}`, JSON.stringify(next));
              } catch {}
            }}
            sx={{ textTransform: 'none' }}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </Button>
        </Box>

        {!collapsed && (
          <>
            <Divider sx={{ my: 1.5, borderColor: '#343a43' }} />
            {loading ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
            ) : (
      <Box display="flex" flexDirection="column" gap={1.25}>
                {tasks.map((t) => {
                  const done = !!statuses[t.code];
                  return (
        <Box key={t.code} display="flex" alignItems="center" justifyContent="space-between" sx={{ p: 1.25, borderRadius: 1.25, border: theme => `1px solid ${theme.palette.divider}` }}>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <span aria-label={done ? 'done' : 'todo'} style={{ fontSize: 18 }}>{done ? '✅' : '⬜️'}</span>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t.title}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.description}</Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => t.link !== '#' && navigate(t.link)}
                        sx={{ textTransform: 'none' }}
                      >
                        Go
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
